import L from 'leaflet';
import 'leaflet-rotate';

/*
 * Поворот карты (плагин leaflet-rotate).
 *
 * Десктоп:  тянуть компас мышью влево/вправо — поворот;
 *           клик по компасу — вернуть север наверх;
 *           Shift + колесо мыши — поворот с шагом 5°.
 * Мобильный: поворот двумя пальцами; тап по компасу — север наверх.
 *
 * Маркеры, подписи, попапы и тултипы НЕ вращаются вместе с картой
 * (остаются читаемыми), вращаются только тайлы и линии.
 */

// Сколько градусов нужно "провернуть" двумя пальцами, прежде чем карта
// начнёт вращаться. Без порога любой pinch-zoom слегка крутит карту.
const TOUCH_ROTATE_THRESHOLD_DEG = 15;

// Опции, которые нужно добавить в L.map(...)
export const ROTATION_MAP_OPTIONS = {
  rotate: true,
  bearing: 0,
  touchRotate: true,
  shiftKeyRotate: true,
  rotateControl: false, // встроенный компас заменяем своим (см. CompassControl)
};

/* ------------------------------------------------------------------ */
/* Компас                                                              */
/* ------------------------------------------------------------------ */

// Встроенный контрол плагина по клику переключает 3 режима
// (вращение пальцами → компас устройства → блокировка), что неочевидно,
// а при перетаскивании "прыгает" к углу, равному смещению мыши.
// Здесь: drag — поворот относительно текущего угла, click — север наверх.
const CompassControl = L.Control.Rotate.extend({
  options: {
    position: 'topleft',
    closeOnZeroBearing: false,
    hideWhenNorth: false, // прятать компас, когда север наверху (для мобильного)
  },

  onAdd: function (map) {
    const container = L.Control.Rotate.prototype.onAdd.call(this, map);
    this._link.title = 'Потяните, чтобы повернуть карту. Клик — север наверх';
    this._applyVisibility();
    return container;
  },

  _handleMouseDown: function (e) {
    L.DomEvent.stop(e);
    this.dragging = true;
    this._dragMoved = false;
    this.dragstartX = e.pageX;
    this._startBearing = this._map.getBearing();
    L.DomEvent
      .on(document, 'mousemove', this._handleMouseDrag, this)
      .on(document, 'mouseup', this._handleMouseUp, this);
  },

  _handleMouseDrag: function (e) {
    if (!this.dragging) { return; }
    const deltaX = e.pageX - this.dragstartX;
    if (Math.abs(deltaX) > 3) { this._dragMoved = true; }
    this._map.setBearing(this._startBearing + deltaX);
  },

  _cycleState: function () {
    if (!this._map) { return; }
    // click после перетаскивания — не сбрасываем поворот
    if (this._dragMoved) {
      this._dragMoved = false;
      return;
    }
    this._map.setBearing(0);
  },

  _restyle: function () {
    L.Control.Rotate.prototype._restyle.call(this);
    this._applyVisibility();
  },

  _applyVisibility: function () {
    if (!this._map || !this._container) { return; }
    if (this.options.hideWhenNorth) {
      this._container.style.display = this._map.getBearing() === 0 ? 'none' : 'block';
    } else {
      this._container.style.display = 'block';
    }
  },
});

/* ------------------------------------------------------------------ */
/* Жесты двумя пальцами                                                */
/* ------------------------------------------------------------------ */

function angleDeg(p1, p2) {
  return Math.atan2(p2.y - p1.y, p2.x - p1.x) * 180 / Math.PI;
}

function normalizeDeg(deg) {
  return ((deg + 540) % 360) - 180; // → [-180, 180)
}

function patchTouchGestures() {
  const proto = L.Map.TouchGestures.prototype;
  if (proto._rotationPatched) { return; }
  proto._rotationPatched = true;

  const origStart = proto._onTouchStart;
  const origMove = proto._onTouchMove;
  const origEnd = proto._onTouchEnd;

  proto._onTouchStart = function (e) {
    if (e.touches && e.touches.length === 2) {
      const p1 = this._map.mouseEventToContainerPoint(e.touches[0]);
      const p2 = this._map.mouseEventToContainerPoint(e.touches[1]);
      this._gestureStartAngle = angleDeg(p1, p2);
      this._rotateUnlocked = false;
    }
    return origStart.call(this, e);
  };

  proto._onTouchMove = function (e) {
    if (this.rotate && this._rotating && !this._rotateUnlocked &&
        e.touches && e.touches.length === 2) {
      const map = this._map;
      const p1 = map.mouseEventToContainerPoint(e.touches[0]);
      const p2 = map.mouseEventToContainerPoint(e.touches[1]);
      const turned = normalizeDeg(angleDeg(p1, p2) - this._gestureStartAngle);

      if (Math.abs(turned) < TOUCH_ROTATE_THRESHOLD_DEG) {
        // Порог не пройден — обрабатываем как обычный pinch-zoom
        this._rotating = false;
        try {
          return origMove.call(this, e);
        } finally {
          this._rotating = true;
        }
      }

      // Порог пройден — начинаем вращение от текущего положения пальцев,
      // чтобы карта не "прыгнула" сразу на 15°.
      this._rotateUnlocked = true;
      const vector = p1.subtract(p2);
      this._startTheta = Math.atan(vector.x / vector.y);
      this._startBearing = map.getBearing();
      if (vector.y < 0) { this._startBearing += 180; }
    }
    return origMove.call(this, e);
  };

  // Подчистка состояния: если два пальца поставили и убрали без движения,
  // плагин не сбрасывает флаг _rotating и не снимает обработчики touchmove/touchend.
  // Видимых сбоев это не вызывало, но состояние лучше держать чистым.
  proto._onTouchEnd = function (e) {
    if (!this._moved) {
      this._zooming = false;
      this._rotating = false;
      L.DomEvent
        .off(document, 'touchmove', this._onTouchMove, this)
        .off(document, 'touchend touchcancel', this._onTouchEnd, this);
      return;
    }
    return origEnd.call(this, e);
  };
}

// Патч должен быть применён до создания карты — делаем это при импорте модуля.
patchTouchGestures();

/* ------------------------------------------------------------------ */

/**
 * Добавляет компас на карту, созданную с ROTATION_MAP_OPTIONS.
 * @param {L.Map} map
 * @param {{ hideWhenNorth?: boolean, position?: string }} [options]
 */
export function setupMapRotation(map, options = {}) {
  const compass = new CompassControl({
    position: options.position || 'topleft',
    hideWhenNorth: !!options.hideWhenNorth,
  });
  compass.addTo(map);
  return compass;
}
